import React, { useEffect, useRef } from 'react';

/**
 * SilkHeader — interactive cloth-simulated silk banner.
 *
 * Physics: Verlet-integrated mass-spring cloth with structural constraints.
 * Rendering: per-quad flat shade from signed area.
 *
 * Interaction:
 *   - cursor velocity → wind with inverse-square distance falloff
 *   - click/drag → elastic tether grab on nearest particle
 *   - wind fades smoothly to zero while grabbing
 *
 * Runs at a fixed simulation rate regardless of monitor refresh, so 120/144 Hz
 * displays don't speed the motion up. See `simulationHz` prop.
 *
 * Props:
 *   - children:      overlay content (title, tagline)
 *   - aspectRatio:   CSS aspect-ratio string (default "4 / 3")
 *   - background:    backdrop behind the silk
 *   - simulationHz:  physics steps per second (default 60). Lower = slower, more zen.
 */
export default function SilkHeader({
  children,
  aspectRatio = '4 / 3',
  background = '#0a0a12',
  simulationHz = 60
}) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // --- config ---
    const COLS = 22;       // thinner cloth
    const ROWS = 34;
    const SPACING = 11;
    const ITERATIONS = 4;
    const GRAVITY = 0.25;
    const DAMPING = 0.985;

    const STEP_MS = 1000 / simulationHz; // fixed physics timestep

    let W = 0, H = 0, DPR = 1;
    let particles = [];
    let constraints = [];

    function buildCloth() {
      particles = [];
      constraints = [];
      const offsetX = W / 2 - (COLS - 1) * SPACING / 2;
      const offsetY = 40;
      for (let y = 0; y < ROWS; y++) {
        for (let x = 0; x < COLS; x++) {
          const px = offsetX + x * SPACING;
          const py = offsetY + y * SPACING;
          particles.push({
            x: px, y: py, oldX: px, oldY: py,
            pinned: y === 0,
            grabbed: false,
            grabOffsetX: 0, grabOffsetY: 0
          });
        }
      }
      for (let y = 0; y < ROWS; y++) {
        for (let x = 0; x < COLS; x++) {
          const i = y * COLS + x;
          if (x < COLS - 1) constraints.push({ a: i, b: i + 1, len: SPACING });
          if (y < ROWS - 1) constraints.push({ a: i, b: i + COLS, len: SPACING });
        }
      }
    }

    function resize() {
      DPR = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      buildCloth();
    }
    resize();
    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    // --- input ---
    const mouse = {
      x: W / 2, y: H / 2, px: W / 2, py: H / 2,
      vx: 0, vy: 0, down: false, grabIndex: -1, windFade: 1
    };

    const getLocal = (clientX, clientY) => {
      const r = canvas.getBoundingClientRect();
      return { x: clientX - r.left, y: clientY - r.top };
    };

    function startGrab() {
      let best = -1, bestD = Infinity;
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (p.pinned) continue;
        const dx = p.x - mouse.x, dy = p.y - mouse.y;
        const d = dx * dx + dy * dy;
        if (d < bestD) { bestD = d; best = i; }
      }
      if (best === -1 || bestD > 900) return;
      mouse.grabIndex = best;
      particles[best].grabbed = true;
      particles[best].grabOffsetX = particles[best].x - mouse.x;
      particles[best].grabOffsetY = particles[best].y - mouse.y;
    }

    function endGrab() {
      for (let i = 0; i < particles.length; i++) particles[i].grabbed = false;
      mouse.grabIndex = -1;
    }

    const onMouseMove = (e) => {
      const pt = getLocal(e.clientX, e.clientY);
      mouse.x = pt.x; mouse.y = pt.y;
    };
    const onMouseDown = (e) => {
      const pt = getLocal(e.clientX, e.clientY);
      mouse.x = pt.x; mouse.y = pt.y;
      mouse.down = true;
      canvas.style.cursor = 'grabbing';
      startGrab();
    };
    const onMouseUp = () => {
      if (mouse.down) endGrab();
      mouse.down = false;
      canvas.style.cursor = 'grab';
    };
    const onTouchMove = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      const pt = getLocal(t.clientX, t.clientY);
      mouse.x = pt.x; mouse.y = pt.y;
    };
    const onTouchStart = (e) => {
      e.preventDefault();
      const t = e.touches[0];
      const pt = getLocal(t.clientX, t.clientY);
      mouse.x = pt.x; mouse.y = pt.y;
      mouse.down = true;
      startGrab();
    };
    const onTouchEnd = () => {
      if (mouse.down) endGrab();
      mouse.down = false;
    };

    canvas.addEventListener('mousemove', onMouseMove);
    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('touchmove', onTouchMove, { passive: false });
    canvas.addEventListener('touchstart', onTouchStart, { passive: false });
    window.addEventListener('touchend', onTouchEnd);

    let visible = true;
    const io = new IntersectionObserver(
      (entries) => { visible = entries[0].isIntersecting; },
      { threshold: 0 }
    );
    io.observe(canvas);

    // --- one physics step (runs at fixed rate) ---
    let ambientPhase = 0;

    function simulate() {
      ambientPhase += STEP_MS * 0.001;

      // update mouse velocity once per sim step (keeps wind consistent)
      mouse.vx = mouse.x - mouse.px;
      mouse.vy = mouse.y - mouse.py;
      mouse.px = mouse.x;
      mouse.py = mouse.y;

      const targetFade = mouse.down ? 0 : 1;
      mouse.windFade += (targetFade - mouse.windFade) * 0.08;

      if (mouse.grabIndex !== -1 && mouse.down) {
        const p = particles[mouse.grabIndex];
        if (p.grabbed) {
          const tx = mouse.x + p.grabOffsetX;
          const ty = mouse.y + p.grabOffsetY;
          p.x += (tx - p.x) * 0.15;
          p.y += (ty - p.y) * 0.15;
        }
      }

      const windStrength = mouse.windFade * 0.08;
      const windVX = mouse.vx * windStrength;
      const windVY = mouse.vy * windStrength;
      const ambientX = Math.sin(ambientPhase * 0.7) * 0.08;
      const ambientY = Math.cos(ambientPhase * 0.5) * 0.04;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        if (p.pinned) continue;
        const vx = (p.x - p.oldX) * DAMPING;
        const vy = (p.y - p.oldY) * DAMPING;
        p.oldX = p.x;
        p.oldY = p.y;
        const dx = p.x - mouse.x;
        const dy = p.y - mouse.y;
        const distSq = dx * dx + dy * dy;
        const falloff = 1 / (1 + distSq * 0.0002);
        p.x += vx + windVX * falloff + ambientX;
        p.y += vy + GRAVITY + windVY * falloff + ambientY;
      }

      for (let iter = 0; iter < ITERATIONS; iter++) {
        for (let i = 0; i < constraints.length; i++) {
          const c = constraints[i];
          const pa = particles[c.a];
          const pb = particles[c.b];
          const dx = pb.x - pa.x;
          const dy = pb.y - pa.y;
          const dist = Math.sqrt(dx * dx + dy * dy) || 0.0001;
          const diff = (dist - c.len) / dist;
          const offX = dx * 0.5 * diff;
          const offY = dy * 0.5 * diff;
          if (!pa.pinned) { pa.x += offX; pa.y += offY; }
          if (!pb.pinned) { pb.x -= offX; pb.y -= offY; }
        }
      }
    }

    function draw() {
      ctx.clearRect(0, 0, W, H);
      ctx.lineWidth = 0.7;

      for (let y = 0; y < ROWS - 1; y++) {
        for (let x = 0; x < COLS - 1; x++) {
          const i = y * COLS + x;
          const p1 = particles[i];
          const p2 = particles[i + 1];
          const p3 = particles[i + COLS];
          const p4 = particles[i + COLS + 1];

          const dx1 = p2.x - p1.x;
          const dy1 = p2.y - p1.y;
          const dx2 = p3.x - p1.x;
          const dy2 = p3.y - p1.y;
          const cross = dx1 * dy2 - dy1 * dx2;
          const shade = Math.max(0, Math.min(1, 0.5 + cross * 0.006));
          const r = Math.floor(80 + shade * 120);
          const g = Math.floor(60 + shade * 60);
          const b = Math.floor(140 + shade * 110);
          ctx.fillStyle = `rgb(${r},${g},${b})`;

          ctx.beginPath();
          ctx.moveTo(p1.x, p1.y);
          ctx.lineTo(p2.x, p2.y);
          ctx.lineTo(p4.x, p4.y);
          ctx.lineTo(p3.x, p3.y);
          ctx.closePath();
          ctx.fill();
        }
      }

      ctx.strokeStyle = 'rgba(255,255,255,0.06)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < constraints.length; i++) {
        const c = constraints[i];
        const pa = particles[c.a];
        const pb = particles[c.b];
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }

      if (mouse.down) {
        ctx.strokeStyle = 'rgba(255,180,220,0.6)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, 8, 0, Math.PI * 2);
        ctx.stroke();
      }
    }

    // --- fixed-timestep loop (decouples physics from refresh rate) ---
    let lastTime = performance.now();
    let accumulator = 0;
    const MAX_ACCUMULATED = STEP_MS * 5; // avoid spiral-of-death after tab switch

    function frame() {
      rafRef.current = requestAnimationFrame(frame);
      if (!visible) {
        lastTime = performance.now();
        accumulator = 0;
        return;
      }

      const now = performance.now();
      let delta = now - lastTime;
      lastTime = now;
      if (delta > MAX_ACCUMULATED) delta = MAX_ACCUMULATED;
      accumulator += delta;

      while (accumulator >= STEP_MS) {
        simulate();
        accumulator -= STEP_MS;
      }
      draw();
    }

    rafRef.current = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
      canvas.removeEventListener('mousemove', onMouseMove);
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('touchmove', onTouchMove);
      canvas.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      io.disconnect();
    };
  }, [simulationHz]);

  // --- inline styles ---
  const containerStyle = {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    borderRadius: 4,
    aspectRatio,
    background
  };

  const canvasStyle = {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'block',
    cursor: 'grab'
  };

  const overlayStyle = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    pointerEvents: 'none',
    color: 'rgba(255,255,255,0.92)'
  };

  return (
    <div style={containerStyle}>
      <canvas ref={canvasRef} style={canvasStyle} />
      {children && <div style={overlayStyle}>{children}</div>}
    </div>
  );
}
